import {
  loadConfig,
  getActiveOrganization,
  setActiveOrganization,
  updateOrganizations,
  isConfigured,
  getApiUrl,
} from '../lib/config'
import { fetchUserInfo } from '../lib/api'

export async function orgListCommand(): Promise<void> {
  if (!isConfigured()) {
    console.log('FlightDesk is not configured.')
    console.log('Run: flightdesk init')
    return
  }

  const config = loadConfig()
  const activeOrg = getActiveOrganization()

  console.log('\n📋 Organizations\n')

  if (config.organizations.length === 0) {
    console.log('No organizations found.')
    console.log('Run: flightdesk org refresh')
    return
  }

  for (const org of config.organizations) {
    const isActive = org.id === activeOrg?.id
    const activeTag = isActive ? ' ← active' : ''
    console.log(`  ${isActive ? '●' : '○'} ${org.name}${activeTag}`)
  }

  console.log('')
  console.log('Commands:')
  console.log('  flightdesk org switch <name>   Switch active organization')
  console.log('  flightdesk org refresh         Refresh organizations from API')
  console.log('')
}

export async function orgSwitchCommand(orgIdentifier: string): Promise<void> {
  if (!isConfigured()) {
    console.log('FlightDesk is not configured.')
    console.log('Run: flightdesk init')
    return
  }

  const config = loadConfig()

  // Find org by name or ID (case-insensitive)
  const org = config.organizations.find(
    o => o.id === orgIdentifier || o.name.toLowerCase() === orgIdentifier.toLowerCase()
  )

  if (!org) {
    console.error(`Organization not found: ${orgIdentifier}`)
    console.log('\nAvailable organizations:')
    for (const o of config.organizations) {
      console.log(`  - ${o.name}`)
    }
    process.exit(1)
  }

  const currentOrg = getActiveOrganization()
  if (currentOrg?.id === org.id) {
    console.log(`Already using organization: ${org.name}`)
    return
  }

  setActiveOrganization(org.id)
  console.log(`✅ Switched to organization: ${org.name}`)
}

export async function orgRefreshCommand(): Promise<void> {
  if (!isConfigured()) {
    console.log('FlightDesk is not configured.')
    console.log('Run: flightdesk init')
    return
  }

  const config = loadConfig()

  if (!config.apiKey) {
    console.error('No API key configured.')
    console.log('Run: flightdesk init')
    return
  }

  console.log('Refreshing organizations...')

  try {
    const apiUrl = getApiUrl()
    const userInfo = await fetchUserInfo(config.apiKey, apiUrl)
    const orgs = userInfo.organizations || []

    if (orgs.length === 0) {
      console.log('No organizations found for this user.')
      return
    }

    // Map to our OrganizationInfo format
    const organizations = orgs.map(m => ({
      id: m.organization.id,
      name: m.organization.name,
    }))

    // Check for new orgs
    const currentIds = new Set(config.organizations.map(o => o.id))
    const newOrgs = organizations.filter(o => !currentIds.has(o.id))

    // Check for removed orgs
    const newIds = new Set(organizations.map(o => o.id))
    const removedOrgs = config.organizations.filter(o => !newIds.has(o.id))

    // Update organizations (this also handles active org validation)
    updateOrganizations(organizations)

    console.log(`✅ Found ${organizations.length} organization(s)`)

    if (newOrgs.length > 0) {
      console.log('\n  New organizations:')
      for (const org of newOrgs) {
        console.log(`    + ${org.name}`)
      }
    }

    if (removedOrgs.length > 0) {
      console.log('\n  Removed organizations:')
      for (const org of removedOrgs) {
        console.log(`    - ${org.name}`)
      }
    }

    // Show current active
    const activeOrg = getActiveOrganization()
    if (activeOrg) {
      console.log(`\n  Active: ${activeOrg.name}`)
    }
  } catch (error) {
    console.error(`❌ Failed to refresh: ${error}`)
  }
}
